// src/services/azurePersonsService.js
// Azure Table Storage Service für Personen

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectPersons",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Helper functions for data cleaning
const cleanValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
};

// Hilfsfunktion für eindeutige IDs
const generatePersonId = () => {
  return `person_${Date.now()}`;
};

// Alle Personen laden (CRM-erweitert)
export const getAllPersons = async () => {
  try {
    let allPersons = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allPersons = allPersons.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    const persons = allPersons.map((item) => ({
      id: item.RowKey,
      firstName: item.FirstName,
      lastName: item.LastName,
      email: item.Email || "",
      phone: item.Phone || "",
      mobile: item.Mobile || "",
      position: item.Position || "",
      notes: item.Notes || "",
      // CRM-Felder mit Fallbacks
      organization: item.Organization || "",
      anrede: item.Anrede || "du",
      geburtsdatum: item.Geburtsdatum || "",
      newsletter: item.Newsletter === "true" || item.Newsletter === true,
      weihnachtskarte:
        item.Weihnachtskarte === "true" || item.Weihnachtskarte === true,
      addressStrasse: item.AddressStrasse || "",
      addressPlz: item.AddressPlz || "",
      addressOrt: item.AddressOrt || "",
      addressLand: item.AddressLand || "Schweiz",
      tags: item.Tags || "",
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));

    return persons;
  } catch (error) {
    console.error("Load Persons Error:", error);
    return [];
  }
};

// Einzelne Person laden
export const getPerson = async (personId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='person',RowKey='${personId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const item = await response.json();

    return {
      id: item.RowKey,
      firstName: item.FirstName,
      lastName: item.LastName,
      email: item.Email || "",
      phone: item.Phone || "",
      mobile: item.Mobile || "",
      position: item.Position || "",
      notes: item.Notes || "",
      organization: item.Organization || "",
      anrede: item.Anrede || "du",
      geburtsdatum: item.Geburtsdatum || "",
      newsletter: item.Newsletter === "true" || item.Newsletter === true,
      weihnachtskarte:
        item.Weihnachtskarte === "true" || item.Weihnachtskarte === true,
      addressStrasse: item.AddressStrasse || "",
      addressPlz: item.AddressPlz || "",
      addressOrt: item.AddressOrt || "",
      addressLand: item.AddressLand || "Schweiz",
      tags: item.Tags || "",
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    };
  } catch (error) {
    console.error("Load Person Error:", error);
    return null;
  }
};

// Person erstellen (CRM-erweitert)
export const createPerson = async (personData) => {
  try {
    const personId = generatePersonId();
    const now = new Date().toISOString();

    // Validate required fields
    if (!personData.firstName || !personData.lastName) {
      throw new Error("Vor- und Nachname sind erforderlich");
    }

    const entity = {
      PartitionKey: "person",
      RowKey: personId,
      FirstName: cleanValue(personData.firstName),
      LastName: cleanValue(personData.lastName),
      Email: cleanValue(personData.email),
      Phone: cleanValue(personData.phone),
      Mobile: cleanValue(personData.mobile),
      Position: cleanValue(personData.position),
      Notes: cleanValue(personData.notes),
      // CRM-Felder
      Organization: cleanValue(personData.organization),
      Anrede: cleanValue(personData.anrede || "du"),
      Geburtsdatum: cleanValue(personData.geburtsdatum),
      Newsletter: personData.newsletter ? "true" : "false",
      Weihnachtskarte: personData.weihnachtskarte ? "true" : "false",
      AddressStrasse: cleanValue(personData.addressStrasse || ""),
      AddressPlz: cleanValue(personData.addressPlz || ""),
      AddressOrt: cleanValue(personData.addressOrt || ""),
      AddressLand: cleanValue(personData.addressLand || "Schweiz"),
      Tags: cleanValue(personData.tags),
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='person',RowKey='${personId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
    }

    return {
      id: personId,
      ...personData,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Create Person Error:", error);
    throw error;
  }
};

// Person aktualisieren (CRM-erweitert)
export const updatePerson = async (personId, personData) => {
  try {
    const now = new Date().toISOString();

    // Validate required fields
    if (!personData.firstName || !personData.lastName) {
      throw new Error("Vor- und Nachname sind erforderlich");
    }

    const entity = {
      PartitionKey: "person",
      RowKey: personId,
      FirstName: cleanValue(personData.firstName),
      LastName: cleanValue(personData.lastName),
      Email: cleanValue(personData.email),
      Phone: cleanValue(personData.phone),
      Mobile: cleanValue(personData.mobile),
      Position: cleanValue(personData.position),
      Notes: cleanValue(personData.notes),
      // CRM-Felder
      Organization: cleanValue(personData.organization),
      Anrede: cleanValue(personData.anrede || "du"),
      Geburtsdatum: cleanValue(personData.geburtsdatum),
      Newsletter: personData.newsletter ? "true" : "false",
      Weihnachtskarte: personData.weihnachtskarte ? "true" : "false",
      AddressStrasse: cleanValue(personData.addressStrasse || ""),
      AddressPlz: cleanValue(personData.addressPlz || ""),
      AddressOrt: cleanValue(personData.addressOrt || ""),
      AddressLand: cleanValue(personData.addressLand || "Schweiz"),
      Tags: cleanValue(personData.tags),
      UpdatedAt: now,
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='person',RowKey='${personId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status} - ${errorText}`);
    }

    return {
      id: personId,
      ...personData,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Update Person Error:", error);
    throw error;
  }
};

// Person löschen
export const deletePerson = async (personId) => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='person',RowKey='${personId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Person Error:", error);
    throw error;
  }
};

// Personen suchen
export const searchPersons = async (searchTerm) => {
  try {
    // Note: Azure Table Storage doesn't support LIKE queries
    // We'll load all and filter client-side for simplicity
    const allPersons = await getAllPersons();

    const searchLower = searchTerm.toLowerCase();
    return allPersons.filter(
      (person) =>
        person.firstName.toLowerCase().includes(searchLower) ||
        person.lastName.toLowerCase().includes(searchLower) ||
        person.email.toLowerCase().includes(searchLower) ||
        person.position.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error("Search Persons Error:", error);
    return [];
  }
};

// Personen nach Name sortiert laden
export const getPersonsSorted = async () => {
  try {
    const persons = await getAllPersons();
    return persons.sort((a, b) => {
      const nameA = `${a.lastName}, ${a.firstName}`.toLowerCase();
      const nameB = `${b.lastName}, ${b.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  } catch (error) {
    console.error("Load Sorted Persons Error:", error);
    return [];
  }
};

// Person mit vollständigem Namen finden
export const findPersonByName = async (firstName, lastName) => {
  try {
    const allPersons = await getAllPersons();
    return allPersons.find(
      (person) =>
        person.firstName.toLowerCase() === firstName.toLowerCase() &&
        person.lastName.toLowerCase() === lastName.toLowerCase()
    );
  } catch (error) {
    console.error("Find Person by Name Error:", error);
    return null;
  }
};

// Konfiguration aktualisieren
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
